import { useContributorMediaUpload } from '@hooks/useContributorMediaUpload.ts';
import { canUpdateContributorMedia } from '@utils/authUtils.ts';
import { showToast } from '@utils/toast.ts';
import { memo, useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Album } from '../../types';

interface Contributor {
  id: string;
  givenName: string;
  thumbnailUrl?: string;
  videoDesktopUrl?: string;
  videoMobileUrl?: string;
}

interface ContributorMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  contributor: Contributor;
  album: Album;
}

interface PendingFile {
  file: File;
  preview: string;
}

export const ContributorMediaModal = memo(
  ({ isOpen, onClose, contributor, album }: ContributorMediaModalProps) => {
    const auth = useAuth();
    const { uploadFile, saveChanges } = useContributorMediaUpload();

    // Store pending files with their previews (not uploaded yet)
    const [pendingFiles, setPendingFiles] = useState<{
      thumbnail: PendingFile | null;
      videoDesktop: PendingFile | null;
      videoMobile: PendingFile | null;
    }>({
      thumbnail: null,
      videoDesktop: null,
      videoMobile: null,
    });

    const [isSaving, setIsSaving] = useState(false);

    // Track which files are currently uploading
    const [uploadingFiles, setUploadingFiles] = useState({
      thumbnail: false,
      videoDesktop: false,
      videoMobile: false,
    });

    const handleFileSelect = useCallback(
      (file: File, mediaType: 'thumbnail' | 'desktop' | 'mobile') => {
        const uploadKey =
          mediaType === 'desktop'
            ? 'videoDesktop'
            : mediaType === 'mobile'
            ? 'videoMobile'
            : 'thumbnail';

        // Create a preview URL for the selected file
        const preview = URL.createObjectURL(file);

        // Store the pending file with its preview
        setPendingFiles((prev) => ({
          ...prev,
          [uploadKey]: { file, preview },
        }));

        console.log(`📁 File selected for ${mediaType}:`, file.name);
      },
      [],
    );

    const handleSave = useCallback(async () => {
      // Check if there are any pending files to upload
      const filesToUpload = Object.entries(pendingFiles).filter(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, pendingFile]) => pendingFile !== null,
      );

      if (filesToUpload.length === 0) {
        showToast.info('No changes to save');
        return;
      }

      setIsSaving(true);

      try {
        // Upload all pending files in parallel
        const uploadPromises = filesToUpload.map(([key, pendingFile]) => {
          if (!pendingFile) return Promise.resolve(null);

          const mediaType =
            key === 'videoDesktop'
              ? 'desktop'
              : key === 'videoMobile'
              ? 'mobile'
              : 'thumbnail';

          // Mark this file as uploading
          setUploadingFiles((prev) => ({
            ...prev,
            [key]: true,
          }));

          console.log(`📤 Uploading ${mediaType}...`);
          return uploadFile(pendingFile.file, contributor.id, mediaType)
            .then((result) => {
              // Mark upload complete for this file
              setUploadingFiles((prev) => ({
                ...prev,
                [key]: false,
              }));
              return {
                key,
                mediaType,
                url: result.url,
              };
            })
            .catch((error) => {
              // Mark upload complete even on error
              setUploadingFiles((prev) => ({
                ...prev,
                [key]: false,
              }));
              console.error(`Failed to upload ${mediaType}:`, error);
              throw error;
            });
        });

        // Wait for all uploads to complete
        const uploadResults = await Promise.all(uploadPromises);

        // Build the media data object from upload results
        const mediaData: any = {};
        uploadResults.forEach((result) => {
          if (result) {
            if (result.key === 'thumbnail') {
              mediaData.thumbnailUrl = result.url;
            } else if (result.key === 'videoDesktop') {
              mediaData.videoDesktopUrl = result.url;
            } else if (result.key === 'videoMobile') {
              mediaData.videoMobileUrl = result.url;
            }
          }
        });

        // Save the changes to the contributor
        // await saveChanges(contributor.id, mediaData);

        showToast.success('Changes saved successfully!');
        onClose();
      } catch (error) {
        console.error('Failed to save changes:', error);
        showToast.error('Failed to save changes. Please try again.');
      } finally {
        setIsSaving(false);
      }
    }, [pendingFiles, contributor.id, uploadFile, saveChanges, onClose]);

    const handleThumbnailChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleFileSelect(file, 'thumbnail');
        }
      },
      [handleFileSelect],
    );

    const handleVideoDesktopChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleFileSelect(file, 'desktop');
        }
      },
      [handleFileSelect],
    );

    const handleVideoMobileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleFileSelect(file, 'mobile');
        }
      },
      [handleFileSelect],
    );

    const handleDownload = useCallback(
      async (url: string, filename: string) => {
        try {
          // Try to fetch the file and create a blob for direct download
          const response = await fetch(url);
          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            window.URL.revokeObjectURL(blobUrl);
          } else {
            // Fallback to direct link if fetch fails
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } catch (error) {
          console.error('Download failed:', error);
          // Fallback to direct link
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      },
      [],
    );

    if (!isOpen) return null;

    // Security check - only allow authorized admins
    if (!canUpdateContributorMedia(auth.user)) {
      showToast.error('Unauthorized access');
      onClose();
      return null;
    }

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-[9999] p-4 pt-20">
        <div className="bg-black/90 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl w-full max-w-md h-[60vh] flex flex-col">
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
            <div>
              <h2 className="text-left text-xl font-semibold text-white font-[NapsterSans]">
                Update Media
              </h2>
              <p className="text-sm text-white/60 font-[NapsterSans] mt-1">
                {contributor.givenName} - {album.albumName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors duration-200">
              <svg
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="p-6 space-y-8 overflow-y-auto flex-1">
            {/* Thumbnail Upload */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-white font-[NapsterSans] text-left">
                Thumbnail Image
              </label>
              <div className="flex items-start gap-4">
                <div className="w-40 rounded-lg overflow-hidden bg-gray-700 flex-shrink-0">
                  <img
                    src={
                      pendingFiles.thumbnail?.preview ||
                      contributor.thumbnailUrl
                    }
                    alt={contributor.givenName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col items-start gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    disabled={isSaving || uploadingFiles.thumbnail}
                    className="hidden"
                    id="thumbnail-upload"
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById(
                        'thumbnail-upload',
                      ) as HTMLInputElement;
                      input?.click();
                    }}
                    disabled={uploadingFiles.thumbnail}
                    className="inline-flex items-center justify-start px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50">
                    Upload Thumbnail
                  </button>
                  <button
                    onClick={() =>
                      handleDownload(
                        contributor.thumbnailUrl ? contributor.thumbnailUrl! : '',
                        `${contributor.givenName}-thumbnail.jpg`,
                      )
                    }
                    className="inline-flex items-center justify-start px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors duration-200">
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="mr-2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download
                  </button>
                  {uploadingFiles.thumbnail && (
                    <p className="text-xs text-blue-400">⏳ Uploading...</p>
                  )}
                  {pendingFiles.thumbnail && !uploadingFiles.thumbnail && (
                    <p className="text-xs text-orange-400">📷 Image selected</p>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Video Upload */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-white font-[NapsterSans] text-left">
                Desktop Video
              </label>
              <div className="flex items-start gap-4">
                <div className="w-40 rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0">
                  {pendingFiles.videoDesktop?.preview ||
                  contributor.videoDesktopUrl ? (
                    <video
                      src={
                        pendingFiles.videoDesktop?.preview ||
                        contributor.videoDesktopUrl
                      }
                      className="w-full h-full object-cover"
                      muted
                    />
                  ) : (
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-white/40">
                      <polygon points="23 7 16 12 23 17 23 7" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                  )}
                </div>
                <div className="flex flex-col items-start gap-3">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoDesktopChange}
                    disabled={isSaving || uploadingFiles.videoDesktop}
                    className="hidden"
                    id="desktop-video-upload"
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById(
                        'desktop-video-upload',
                      ) as HTMLInputElement;
                      input?.click();
                    }}
                    disabled={uploadingFiles.videoDesktop}
                    className="inline-flex items-center justify-start px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50">
                    Upload Desktop Video
                  </button>
                  {contributor.videoDesktopUrl && (
                    <button
                      onClick={() =>
                        handleDownload(
                          contributor.videoDesktopUrl!,
                          `${contributor.givenName}-desktop-video.mp4`,
                        )
                      }
                      className="inline-flex items-center justify-start px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors duration-200">
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        className="mr-2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7,10 12,15 17,10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download
                    </button>
                  )}
                  {uploadingFiles.videoDesktop && (
                    <p className="text-xs text-blue-400">⏳ Uploading...</p>
                  )}
                  {pendingFiles.videoDesktop &&
                    !uploadingFiles.videoDesktop && (
                      <p className="text-xs text-orange-400">
                        🎬 Video selected
                      </p>
                    )}
                </div>
              </div>
            </div>

            {/* Mobile Video Upload */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-white font-[NapsterSans] text-left">
                Mobile Video
              </label>
              <div className="flex items-start gap-4">
                <div className="w-40 rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0">
                  {pendingFiles.videoMobile?.preview ||
                  contributor.videoMobileUrl ? (
                    <video
                      src={
                        pendingFiles.videoMobile?.preview ||
                        contributor.videoMobileUrl
                      }
                      className="w-full h-full object-cover"
                      muted
                    />
                  ) : (
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-white/40">
                      <polygon points="23 7 16 12 23 17 23 7" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                  )}
                </div>
                <div className="flex flex-col items-start gap-3">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoMobileChange}
                    disabled={isSaving || uploadingFiles.videoMobile}
                    className="hidden"
                    id="mobile-video-upload"
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById(
                        'mobile-video-upload',
                      ) as HTMLInputElement;
                      input?.click();
                    }}
                    disabled={uploadingFiles.videoMobile}
                    className="inline-flex items-center justify-start px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50">
                    Upload Mobile Video
                  </button>
                  {contributor.videoMobileUrl && (
                    <button
                      onClick={() =>
                        handleDownload(
                          contributor.videoMobileUrl!,
                          `${contributor.givenName}-mobile-video.mp4`,
                        )
                      }
                      className="inline-flex items-center justify-start px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors duration-200">
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        className="mr-2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7,10 12,15 17,10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download
                    </button>
                  )}
                  {uploadingFiles.videoMobile && (
                    <p className="text-xs text-blue-400">⏳ Uploading...</p>
                  )}
                  {pendingFiles.videoMobile && !uploadingFiles.videoMobile && (
                    <p className="text-xs text-orange-400">🎬 Video selected</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="flex justify-between items-center p-6 border-t border-white/10 flex-shrink-0">
            <div className="text-sm text-white/60"></div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="px-6 py-2 bg-white/10 hover:bg-white/20 disabled:bg-white/5 disabled:hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors duration-200">
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={
                  isSaving || !Object.values(pendingFiles).some(Boolean)
                }
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors duration-200">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ContributorMediaModal.displayName = 'ContributorMediaModal';

export default ContributorMediaModal;
